import React from "react";
import { format } from "date-fns";
import { TrendingUp, TrendingDown, Minus, ExternalLink, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { motion, AnimatePresence } from "framer-motion";

export default function AnalysisHistory({ analyses, onSelectAnalysis }) {
    if (!analyses || analyses.length === 0) {
        return (
            <Card className="border-gray-800 bg-zinc-950/80 backdrop-blur shadow-2xl">
                <CardContent className="py-12 text-center">
                    <p className="text-gray-500">No analysis history yet. Upload a chart to get started.</p>
                </CardContent>
            </Card>
        );
    }

    const getSignalBadgeColor = (signal) => {
        switch (signal) {
            case "BUY":
                return "bg-emerald-500/20 text-emerald-400 border-emerald-500/40";
            case "SELL":
                return "bg-red-500/20 text-red-400 border-red-500/40";
            default:
                return "bg-gray-500/20 text-gray-400 border-gray-500/40";
        }
    };

    const getSignalIcon = (signal) => {
        switch (signal) {
            case "BUY":
                return <TrendingUp className="w-3 h-3" />;
            case "SELL":
                return <TrendingDown className="w-3 h-3" />;
            default:
                return <Minus className="w-3 h-3" />;
        }
    };

    return (
        <Card className="border-gray-800 bg-zinc-950/80 backdrop-blur shadow-2xl">
            <CardHeader className="border-b border-gray-900">
                <CardTitle className="text-white flex items-center gap-2">
                    {/* Replaced raw div pulse with an icon for better consistency if preferred, but keeping user code logic mostly */}
                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                    Analysis History
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 max-h-[600px] overflow-y-auto p-4 custom-scrollbar">
                <AnimatePresence>
                    {analyses.map((analysis, index) => (
                        <motion.div
                            key={analysis.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            onClick={() => onSelectAnalysis(analysis)}
                            className="p-4 bg-zinc-900/50 rounded-lg border border-gray-800 hover:border-amber-500/30 hover:bg-zinc-900/80 transition-all cursor-pointer group"
                        >
                            <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-3">
                                    <Badge variant="outline" className={`${getSignalBadgeColor(analysis.signal)} flex items-center gap-1`}>
                                        {getSignalIcon(analysis.signal)}
                                        {analysis.signal}
                                    </Badge>
                                    <span className="text-amber-500 font-bold">{analysis.pair}</span>
                                </div>
                                <span className="text-xs text-gray-600">
                                    {analysis.created_date ? format(new Date(analysis.created_date), "MMM d, h:mm a") : 'Just now'}
                                </span>
                            </div>
                            <p className="text-sm text-gray-400 line-clamp-2 mb-2">
                                {analysis.explanation}
                            </p>
                            <div className="flex items-center justify-between">
                                <div className="flex gap-2 text-xs">
                                    {analysis.entry_price && (
                                        <span className="text-gray-600">Entry: <span className="text-gray-300 font-semibold">{analysis.entry_price}</span></span>
                                    )}
                                </div>
                                <ExternalLink className="w-3 h-3 text-amber-500/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </CardContent>
        </Card>
    );
}
